// src/pages/Contact.jsx
import React, { useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import contactImage from '../assets/contact-image.png';

function Contact() {
  const { t } = useTranslation();
  const [form, setForm] = useState({ name: '', email: '', message: '' });
  const [status, setStatus] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setStatus(null);
    try {
      await axios.post('https://madadline.com/api/contacts', form);
      setStatus('success');
      setForm({ name: '', email: '', message: '' });
      setTimeout(() => setStatus(null), 4000);
    } catch {
      setStatus('error');
      setTimeout(() => setStatus(null), 4000);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="contact-page">
      <section className="page-hero">
        <div className="page-hero__inner">
          <span className="page-hero__label">{t('contact.heroLabel')}</span>
          <h1 className="page-hero__title">{t('contact.heroTitle')}</h1>
          <p className="page-hero__subtitle">{t('contact.heroSubtitle')}</p>
        </div>
      </section>

      <section className="contact-grid-section">
        <div className="contact-grid">
          <div className="contact-image">
            <img src={contactImage} alt="MADAD" loading="lazy" />
            <div className="contact-image__overlay" />
            <div className="contact-image__text">
              <span className="contact-image__heart">♥</span>
              <h3>{t('contact.imageTitle')}</h3>
              <p>{t('contact.imageSubtitle')}</p>
            </div>
          </div>

          <div className="contact-form-side">
            {status === 'success' && (
              <div className="contact-alert contact-alert--success">✓ {t('contact.successMsg')}</div>
            )}
            {status === 'error' && (
              <div className="contact-alert contact-alert--error">✗ {t('contact.errorMsg')}</div>
            )}

            <form className="contact-form" onSubmit={handleSubmit}>
              <div className="contact-form__group">
                <label htmlFor="name">{t('contact.labelName')}</label>
                <input id="name" name="name" type="text" value={form.name} onChange={handleChange} placeholder={t('contact.placeholderName')} required />
              </div>

              <div className="contact-form__group">
                <label htmlFor="email">{t('contact.labelEmail')}</label>
                <input id="email" name="email" type="email" value={form.email} onChange={handleChange} placeholder={t('contact.placeholderEmail')} required />
              </div>

              <div className="contact-form__group">
                <label htmlFor="message">{t('contact.labelMessage')}</label>
                <textarea id="message" name="message" rows="5" value={form.message} onChange={handleChange} placeholder={t('contact.placeholderMessage')} required />
              </div>

              <button type="submit" className="contact-form__submit" disabled={submitting}>
                {submitting ? t('contact.sending') : t('contact.submit')}
              </button>
            </form>
          </div>
        </div>
      </section>

      <section className="contact-infos">
        <div className="contact-infos__inner">
          <div className="contact-info-item">
            <div className="contact-info-item__icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
              </svg>
            </div>
            <div className="contact-info-item__body">
              <span className="contact-info-item__label">{t('contact.infoPhone')}</span>
              <a href="tel:+212669428878">+212669428878</a>
            </div>
          </div>

          <div className="contact-info-item">
            <div className="contact-info-item__icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                <rect x="2" y="4" width="20" height="16" rx="2" />
                <path d="m2 6 10 7 10-7" />
              </svg>
            </div>
            <div className="contact-info-item__body">
              <span className="contact-info-item__label">{t('contact.infoEmail')}</span>
              <a href="mailto:madadline@gmail.com">madadline@gmail.com</a>
            </div>
          </div>

          <div className="contact-info-item">
            <div className="contact-info-item__icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                <circle cx="12" cy="10" r="3" />
              </svg>
            </div>
            <div className="contact-info-item__body">
              <span className="contact-info-item__label">{t('contact.infoAddress')}</span>
              <span>{t('contact.addressValue')}</span>
            </div>
          </div>

          <div className="contact-info-item">
            <div className="contact-info-item__icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                <circle cx="12" cy="12" r="10" />
                <polyline points="12 6 12 12 16 14" />
              </svg>
            </div>
            <div className="contact-info-item__body">
              <span className="contact-info-item__label">{t('contact.infoHours')}</span>
              <span>{t('contact.hoursValue')}</span>
            </div>
          </div>
        </div>
      </section>

      <section className="contact-map">
        <div className="contact-map__head">
          <div className="contact-map__head-inner">
            <h3>{t('contact.mapTitle')}</h3>
            <div className="contact-map__socials">
              <a href="https://instagram.com/madadline" target="_blank" rel="noreferrer" aria-label="Instagram">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                  <rect x="3" y="3" width="18" height="18" rx="5" />
                  <circle cx="12" cy="12" r="4" />
                  <circle cx="17.5" cy="6.5" r="1" fill="currentColor" />
                </svg>
              </a>
              <a href="https://wa.me/212669428878" target="_blank" rel="noreferrer" aria-label="WhatsApp">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.71.306 1.263.489 1.694.625.712.227 1.36.195 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
                </svg>
              </a>
            </div>
          </div>
        </div>

        <div className="contact-map__full">
          <iframe
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3323.846!2d-7.5898!3d33.5731!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMzPCsDM0JzIzLjIiTiA3wrAzNS4yMyJX!5e0!3m2!1sfr!2sma!4v1700000000000"
            width="100%"
            height="450"
            style={{ border: 0, display: 'block' }}
            allowFullScreen
            loading="lazy"
            title="MADAD — Casablanca"
          />
        </div>
      </section>
    </main>
  );
}

export default Contact;